import { z } from 'zod';

/**
 * ONE generic integration framework for every external system (Shopify first, QuickBooks Online
 * next). These lists are the single source of truth: the database CHECK constraints
 * (packages/db/src/schema/integrations.ts), the seed, the flow guard in @bnb/integrations-core and
 * the API all read them from here, so they cannot drift apart.
 */

/** External systems the planner can connect to. */
export const SYSTEMS = ['shopify', 'quickbooks'] as const;
export const systemSchema = z.enum(SYSTEMS);
export type SystemId = z.infer<typeof systemSchema>;

/**
 * Per-system KILL SWITCH (table integration_system_setting). It caps every flow of that system:
 * - `off`        nothing talks to the system at all
 * - `read_only`  inbound flows may run; nothing is ever written to the system
 * - `dry_run`    flows compute and record what they WOULD do; nothing is applied or sent
 * - `live`       each flow follows its own mode
 */
export const SYSTEM_MODES = ['off', 'read_only', 'dry_run', 'live'] as const;
export const systemModeSchema = z.enum(SYSTEM_MODES);
export type SystemMode = z.infer<typeof systemModeSchema>;

/** Per-flow mode (table integration_flow_setting). New flows start `off`. */
export const FLOW_MODES = ['off', 'dry_run', 'live'] as const;
export const flowModeSchema = z.enum(FLOW_MODES);
export type FlowMode = z.infer<typeof flowModeSchema>;

/**
 * The flows each system has, i.e. the rows seeded into integration_flow_setting. A flow moves data
 * in ONE direction, so it can be switched on, dry-run or cut over on its own date.
 * Adding a flow = add it here AND seed it (packages/db/src/seed/reference-data.ts); a db test
 * checks the two lists match.
 */
export const FLOWS = {
  shopify: ['orders.inbound', 'catalog.outbound', 'inventory.outbound'],
  quickbooks: ['costs.bills', 'cash.payments'],
} as const satisfies Record<SystemId, readonly string[]>;
export type FlowId<S extends SystemId = SystemId> = (typeof FLOWS)[S][number];

export const FLOW_DIRECTIONS = ['inbound', 'outbound'] as const;
export type FlowDirection = (typeof FLOW_DIRECTIONS)[number];

/**
 * Which way each flow moves data. `inbound` = external system → planner (allowed while the system
 * is `read_only`); `outbound` = planner → external system (never while `read_only`).
 * QuickBooks flows are inbound only: bills/expenses become per-flock costs, payments become cash
 * actuals. Shopify sales do NOT flow into QuickBooks.
 */
const FLOW_DIRECTION_BY_FLOW: { [S in SystemId]: Record<FlowId<S>, FlowDirection> } = {
  shopify: {
    'orders.inbound': 'inbound',
    'catalog.outbound': 'outbound',
    'inventory.outbound': 'outbound',
  },
  quickbooks: {
    'costs.bills': 'inbound',
    'cash.payments': 'inbound',
  },
};

/** Direction of a flow, or null for a flow this system does not have. */
export function flowDirection(system: SystemId, flow: string): FlowDirection | null {
  const directions: Record<string, FlowDirection> = FLOW_DIRECTION_BY_FLOW[system];
  return Object.hasOwn(directions, flow) ? (directions[flow] ?? null) : null;
}

/** True when `flow` is one of `system`'s flows. */
export function isFlowOf<S extends SystemId>(system: S, flow: string): flow is FlowId<S> {
  return (FLOWS[system] as readonly string[]).includes(flow);
}

/**
 * Kill-switch defaults seeded into integration_system_setting. Shopify starts in dry_run (nobody
 * writes to the live store by accident); QuickBooks starts read_only (it only flows INTO the planner).
 */
export const DEFAULT_SYSTEM_MODES: Record<SystemId, SystemMode> = {
  shopify: 'dry_run',
  quickbooks: 'read_only',
};

/** integration_connection.environment: QBO sandbox/production, Shopify dev store/production. */
export const CONNECTION_ENVIRONMENTS = ['sandbox', 'dev_store', 'production'] as const;
export const connectionEnvironmentSchema = z.enum(CONNECTION_ENVIRONMENTS);
export type ConnectionEnvironment = z.infer<typeof connectionEnvironmentSchema>;

/**
 * integration_connection.status. `needs_reconnect` = the refresh token was rejected (invalid_grant)
 * or revoked: an admin must press "Reconnect"; flows for that connection stop until then.
 */
export const CONNECTION_STATUSES = [
  'pending',
  'connected',
  'needs_reconnect',
  'disconnected',
  'error',
] as const;
export const connectionStatusSchema = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/** integration_inbox.status (one row per inbound message; dedupe on (system, dedupe_key)). */
export const INBOX_STATUSES = ['pending', 'processed', 'failed', 'ignored'] as const;
export type InboxStatus = (typeof INBOX_STATUSES)[number];

/**
 * integration_outbox.status (one row per desired external change).
 * `dry_run` = recorded, not sent (a mode said dry_run); `superseded` = a newer row replaced it.
 */
export const OUTBOX_STATUSES = ['pending', 'dry_run', 'sent', 'failed', 'superseded'] as const;
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

/** integration_issue: problems a person must look at (the "Sync issues" list). */
export const ISSUE_SEVERITIES = ['info', 'warning', 'error'] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];
export const ISSUE_KINDS = [
  'needs_mapping',
  'drift',
  'auth',
  'rate_limit',
  'validation',
  'other',
] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];
export const ISSUE_STATUSES = ['open', 'resolved', 'ignored'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

/** integration_reconciliation_run.status. */
export const RECONCILIATION_STATUSES = ['running', 'succeeded', 'failed'] as const;

/** accounting_period_lock.source: mirrored from QBO's BookCloseDate, or set by hand. */
export const PERIOD_LOCK_SOURCES = ['quickbooks', 'manual'] as const;

/**
 * sales_channel.revenue_poster — the structural guard that EXACTLY ONE system posts each revenue
 * stream into QuickBooks. The planner only ever posts channels marked 'planner'; it never posts
 * Shopify revenue ('shopify_connector' = Intuit's connector or A2X does).
 */
export const REVENUE_POSTERS = ['none', 'planner', 'shopify_connector', 'other'] as const;
export type RevenuePoster = (typeof REVENUE_POSTERS)[number];

/**
 * SQS message on the generic integration-INBOUND queue (standard queue + DLQ). A thin trigger:
 * the worker dedupes on (system, dedupeKey) in integration_inbox, then re-fetches current state
 * from the system. Never trust a payload for business data.
 */
export const integrationInboundMessageSchema = z.object({
  system: systemSchema,
  /** integration_connection.id the message belongs to (unknown accounts are dropped earlier). */
  connectionId: z.uuid(),
  /** E.g. Shopify X-Shopify-Webhook-Id, or '<type>:<id>:<SyncToken>' for a QBO CDC change. */
  dedupeKey: z.string().min(1).max(500),
  /** Source topic/event type, e.g. 'orders/updated' or 'qbo.bill.updated.v1'. */
  topic: z.string().min(1).max(200),
  /** What changed (Shopify GID, '<QBO type>:<id>'), when the source says. */
  resourceRef: z.string().min(1).max(500).optional(),
  receivedAt: z.iso.datetime({ offset: true }),
});
export type IntegrationInboundMessage = z.infer<typeof integrationInboundMessageSchema>;

/**
 * SQS message on the generic integration-OUTBOUND FIFO queue (MessageGroupId
 * '<system>:<connectionId>' = one lane per connected account). The outbox ROW is the source of truth.
 */
export const integrationOutboundMessageSchema = z.object({
  outboxId: z.uuid(),
});
export type IntegrationOutboundMessage = z.infer<typeof integrationOutboundMessageSchema>;

/** FIFO MessageGroupId for an outbound message: one ordered lane per connected account. */
export function outboundMessageGroupId(system: SystemId, connectionId: string): string {
  return `${system}:${connectionId}`;
}
